/**
 * Data-defined object types — the three READS the Objects surface needs, plus
 * the create mutation.
 *
 * `create` is RO-2-03's conversion of the /database/objects/new wizard's server
 * action. It delegates to core's `createObjectType` (origin 'user'), the SAME
 * validation + reserved-slug refusal the agent `object_type_create` MCP tool
 * uses, so both planes share one seam. It mirrors the discriminated result core
 * returns so the wizard can surface reason codes as inline errors rather than a
 * thrown 500 — matching `CreateObjectTypeActionResult` in the original
 * `excellent/app/database/objects/new/actions.ts`.
 *
 * ── `list` / `detail` / `kinds` are RO-3-11 (#44304) ──
 *
 * They exist because three `/database` pages were SERVER components that read
 * the object-kind inventory straight out of the vault, so the port had nothing
 * to call:
 *
 *   `list`   → `/database/objects`   (and `ObjectsIndexClient`'s `rows` prop,
 *              which `docs/rollover/inventory/shared-render.md` §5 records as
 *              "an `objects.list` Operation … owed by RO-3-11". The three
 *              `*ScopeActions` in `client/shared/database/` take the same rows,
 *              so this also unblocks RO-3-04 / RO-3-06 / RO-3-07.)
 *   `detail` → `/database/objects/$kind`
 *   `kinds`  → `/database/views`'s kind picker + per-kind filterable fields
 *
 * All three are pure reads over `object_types` / the entity registry / custom
 * fields. Writing them is what §5 means by "supplying that prop from a
 * `fetch()` is not a workaround, it is a rail violation — write the contract
 * instead" (CLAUDE.md hard rule 1).
 *
 * The row SHAPES here are the shapes the pages already assembled — e.g.
 * `objectKindRowSchema` is `ObjectKindRow` from
 * `excellent/app/database/objects/_index-client.tsx`, unchanged — so the
 * endpoint moved WHERE the assembly happens without changing WHAT it produces.
 * That keeps the port a transport change (PRD §4.1) rather than a redesign.
 */

import { z } from "zod";
import type { Operation } from "../../types";

export const objectTypeStorageSchema = z.enum(["commitment", "party"]);
export const objectTypePartyBaseSchema = z.enum(["person", "organization"]);

export const createObjectTypeBody = z.object({
  kind: z.string().min(1),
  label: z.string().min(1),
  label_plural: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  icon: z.string().nullable().optional(),
  storage: objectTypeStorageSchema,
  party_base: objectTypePartyBaseSchema.nullable().optional(),
  /** JSON `{states,terminal}` for commitment kinds; absent otherwise. */
  state_machine: z.string().nullable().optional(),
});
export type CreateObjectTypeBody = z.infer<typeof createObjectTypeBody>;

export const createObjectTypeReasonSchema = z.enum([
  "invalid-kind",
  "reserved-kind",
  "kind-exists",
  "party-base-required",
]);

export const createObjectTypeResponse = z.discriminatedUnion("ok", [
  z.object({ ok: z.literal(true), kind: z.string() }),
  z.object({
    ok: z.literal(false),
    reason: createObjectTypeReasonSchema,
    detail: z.string().optional(),
  }),
]);
export type CreateObjectTypeResponse = z.infer<typeof createObjectTypeResponse>;

// `objectTypesCreate` is declared AFTER the reads it invalidates (RO-5-02b /
// #44476) so the value references resolve. Schemas stay above.

// ── RO-3-11 (#44304) — the three reads the ported pages need ────────────────

/**
 * One row of the object-kind inventory. Byte-for-byte the `ObjectKindRow`
 * interface `_index-client.tsx` (now `client/shared/database/objects-index-client.tsx`)
 * already declares, expressed in zod so the endpoint can validate it.
 */
export const objectKindRowSchema = z.object({
  kind: z.string(),
  label: z.string(),
  /** "commitment" | "party" for data-defined kinds; "built-in" for registry kinds. */
  storage: z.enum(["commitment", "party", "built-in"]),
  /** null for built-in kinds (no per-kind record store to count). */
  records: z.number().nullable(),
  fields: z.number(),
  origin: z.enum(["system", "user", "agent"]),
  /** ISO date; null for built-in kinds. */
  created: z.string().nullable(),
  /** Detail route for data-defined kinds; null for read-only built-ins. */
  href: z.string().nullable(),
});
export type ObjectKindRowDto = z.infer<typeof objectKindRowSchema>;

export const objectTypesListResponse = z.object({ rows: z.array(objectKindRowSchema) });
export type ObjectTypesListResponse = z.infer<typeof objectTypesListResponse>;

/**
 * READ — the whole schema in one list: every entity kind, built-in AND
 * data-defined (Data Design PRD §3.4). Ordering is part of the contract, not an
 * accident of the query: data-defined kinds lead (the user's own nouns first),
 * then the built-in library, because that is the IA the page was designed around.
 */
export const objectTypesList: Operation<
  void,
  void,
  void,
  z.infer<typeof objectTypesListResponse>
> = {
  method: "GET",
  path: () => "/api/database/object-types",
  response: objectTypesListResponse,
};

/** The `object_types` row itself, as the detail page's tabs render it. */
export const objectTypeRowSchema = z.object({
  id: z.number(),
  uid: z.string(),
  workspace_id: z.number(),
  kind: z.string(),
  label: z.string(),
  label_plural: z.string().nullable(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  storage: objectTypeStorageSchema,
  party_base: objectTypePartyBaseSchema.nullable(),
  state_machine: z.string().nullable(),
  origin: z.enum(["user", "agent", "system"]),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type ObjectTypeRowDto = z.infer<typeof objectTypeRowSchema>;

/** A field definition on the kind (core `CustomFieldDefRow`). */
export const objectTypeFieldSchema = z.object({
  id: z.number(),
  uid: z.string(),
  slug: z.string(),
  name: z.string(),
  value_type: z.string(),
  description: z.string().nullable(),
  options: z.string().nullable(),
  required: z.number(),
  sort_order: z.number().nullable(),
  archived_at: z.string().nullable(),
});
export type ObjectTypeFieldDto = z.infer<typeof objectTypeFieldSchema>;

/**
 * One record of a data-defined kind, normalized across the two stores. Same
 * `ObjectRecordRow` shape `_detail-client.tsx` declares — the store-specific
 * (commitment vs party) normalization stays server-side, where the original
 * page component did it.
 */
export const objectRecordRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** Lifecycle state (commitment-backed kinds only). */
  state: z.string().nullable(),
  updated: z.string().nullable(),
  /** /o/<uid> for commitment-backed records; null for party overlays. */
  href: z.string().nullable(),
});
export type ObjectRecordRowDto = z.infer<typeof objectRecordRowSchema>;

export const objectTypesDetailQuery = z.object({ kind: z.string().min(1) });

export const objectTypesDetailResponse = z.object({
  type: objectTypeRowSchema,
  fields: z.array(objectTypeFieldSchema),
  records: z.array(objectRecordRowSchema),
});
export type ObjectTypesDetailResponse = z.infer<typeof objectTypesDetailResponse>;

/**
 * READ — one data-defined kind's definition, fields and records. 404s on an
 * unknown kind, which is the port of the original page's `notFound()`.
 */
export const objectTypesDetail: Operation<
  void,
  z.infer<typeof objectTypesDetailQuery>,
  void,
  z.infer<typeof objectTypesDetailResponse>
> = {
  method: "GET",
  path: () => "/api/database/object-types/detail",
  query: objectTypesDetailQuery,
  response: objectTypesDetailResponse,
};

/**
 * A kind a saved view may target, with the fields the filter builder is allowed
 * to offer. Same `KindOption` shape `views/_client.tsx` declares.
 */
export const viewKindOptionSchema = z.object({
  kind: z.string(),
  label: z.string(),
  /** Filterable/selectable core columns for this kind. */
  coreFields: z.array(z.string()),
  /** Custom fields defined on the kind (addressed as cf:<slug>). */
  customFields: z.array(z.object({ slug: z.string(), label: z.string() })),
});
export type ViewKindOptionDto = z.infer<typeof viewKindOptionSchema>;

export const objectTypesKindsResponse = z.object({ kinds: z.array(viewKindOptionSchema) });
export type ObjectTypesKindsResponse = z.infer<typeof objectTypesKindsResponse>;

/**
 * READ — the kind catalog for `/database/views` (Data Design PRD §6.1). The
 * per-kind `coreFields` allowlist is derived server-side deliberately: it is what
 * stops the filter builder offering a field the saved-view compiler would refuse,
 * so it must come from the same place the compiler's own allowlist does.
 */
export const objectTypesKinds: Operation<
  void,
  void,
  void,
  z.infer<typeof objectTypesKindsResponse>
> = {
  method: "GET",
  path: () => "/api/database/object-types/kinds",
  response: objectTypesKindsResponse,
};

/**
 * MUTATION — create a data-defined object type. Declared after the reads so
 * `invalidates` can name them by value (RO-5-02b / #44476).
 *
 * Original: `revalidatePath("/database/objects")` +
 * `revalidatePath(\`/database/objects/${kind}\`)` → list + detail.
 * Deliberately does NOT name `kinds` (the views picker) — that path was not
 * in the original revalidate set.
 */
export const objectTypesCreate: Operation<
  void,
  void,
  z.infer<typeof createObjectTypeBody>,
  z.infer<typeof createObjectTypeResponse>
> = {
  method: "POST",
  path: () => "/api/database/object-types",
  body: createObjectTypeBody,
  response: createObjectTypeResponse,
  // Bare references: both list and detail have `TParams = void`. Detail is
  // query-scoped (`?kind=`), so the family match wildcards every kind — same
  // as the collection shape. Over-invalidates sibling kinds' detail cache on
  // create; that is the bare-reference trade and matches how the mechanism
  // treats every query-filtered collection.
  invalidates: [objectTypesList, objectTypesDetail],
};

export const objectTypes = {
  create: objectTypesCreate,
  list: objectTypesList,
  detail: objectTypesDetail,
  kinds: objectTypesKinds,
};
